/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import HeaderOptions from "../Components/Common/HeaderOptions";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  deleteFromWishlist,
  getProductsByCategoryId,
  postAddToWishlist,
} from "../Utils/api-service";
import ProductLoader from "../Components/Common/ProductLoader";
import { extractIdFromGid } from "../Utils/categories-divide";
import AppContext from "../Utils/appContext";
import ProductCacheContext from "../Utils/productCacheContext";
import ProductBadges from "../Components/ProductBadges";
import {
  addToWishlist,
  getWishlist,
  removeFromWishlist,
} from "../Utils/wishlist-service";
import {
  buildWishlistUiStateMap,
  clearWishlistAnimateInMap,
  getWishlistClassName,
  toggleWishlistInMap,
} from "../Utils/wishlist-ui";
import { showToast } from "../Utils/toast-service";

export default function ProductList() {
  const navigate = useNavigate();
  const { productCache, setProductCache } =
    React.useContext(ProductCacheContext);
  const { showPrice } = React.useContext(AppContext);
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [nextCursor, setNextCursor] = React.useState();
  const [showLoadMore, setShowLoadMore] = React.useState(false);
  const [wishlistState, setWishlistState] = React.useState(() =>
    buildWishlistUiStateMap(getWishlist()),
  );
  let [searchParams] = useSearchParams();
  const categoryName = searchParams.get("categoryName");
  const categoryId = searchParams.get("categoryId");

  const toggleWishlist = async (event, product) => {
    event.preventDefault();
    event.stopPropagation();

    const isActive = Boolean(wishlistState[product.id]?.active);
    setWishlistState((prev) => toggleWishlistInMap(prev, product.id));

    if (isActive) {
      removeFromWishlist(product.id);
      const response = await deleteFromWishlist({
        shopifyProductId: product.id,
      });
      if (
        response?.status !== 200 ||
        response?.data?.removed === false
      ) {
        setWishlistState((prev) => ({
          ...prev,
          [product.id]: buildWishlistUiStateMap([product.id])[product.id],
        }));
        addToWishlist(product.id);
      }
      return;
    }

    addToWishlist(product.id);
    const response = await postAddToWishlist({
      shopifyProductId: product.id,
      source: "PLP",
    });

    if (response?.status !== 201 && response?.status !== 200) {
      setWishlistState((prev) => toggleWishlistInMap(prev, product.id));
      removeFromWishlist(product.id);
      return;
    }

    showToast("Added to wishlist");

    // Keep filled heart; drop animate after firework so it won't replay
    setTimeout(() => {
      setWishlistState((prev) => clearWishlistAnimateInMap(prev, product.id));
    }, 700);
  };

  React.useEffect(() => {
    if (
      productCache.products.length > 0 &&
      productCache.categoryId === categoryId
    ) {
      setProducts(productCache.products);
      setNextCursor(productCache.nextCursor);
      setShowLoadMore(productCache.showLoadMore);

      // Restore scroll
      setTimeout(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, productCache.scrollY);
        });
      }, 100);
    } else {
      fetchProducts(categoryId); // Load fresh
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  // React.useEffect(() => {
  //   if (categoryId) {
  //     fetchProducts(categoryId);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [categoryId]);

  const fetchProducts = async (id) => {
    setLoading(true);
    const sendId = categoryId || id;

    try {
      const data = await getProductsByCategoryId(sendId, nextCursor);
      if (data.status === 200) {
        const ps = data?.data?.collection?.products?.nodes || [];
        setProducts((prev) => [...prev, ...ps]);
        setShowLoadMore(
          data?.data?.collection?.products?.pageInfo?.hasNextPage,
        );
        setNextCursor(data?.data?.collection?.products?.pageInfo?.endCursor);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const loadMore = () => {
    if (categoryId) {
      fetchProducts(categoryId);
    }
  };

  const [infiniteRef] = useInfiniteScroll({
    loading: loading,
    hasNextPage: showLoadMore,
    onLoadMore: loadMore,
    // When there is an error, we stop infinite loading.
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: "0px 0px 400px 0px",
  });

  const goToProduct = (id) => {
    setProductCache({
      products,
      nextCursor,
      scrollY: window.scrollY,
      categoryId,
      showLoadMore,
    });
    const productId = extractIdFromGid(id);
    navigate("/product?productId=" + productId);
  };
  return (
    <>
      <header>
        <div className="back-links">
          <a onClick={() => navigate(-1)}>
            <i className="iconly-Arrow-Left icli"></i>
            <div className="content">
              <h2>
                <span>{categoryName || "Category"}</span>
              </h2>
            </div>
          </a>
        </div>
        <HeaderOptions />
      </header>
      <div
        className={
          categoryName?.length > 28
            ? "search-panel estimate-top-space px-15"
            : "search-panel top-space px-15"
        }
      ></div>

      <section className="px-15 lg-t-space">
        <div className="row gy-3 gx-3">
          {products?.length > 0 &&
            products?.map((product, idx) => {
              return (
                <div key={idx} className="col-6">
                  <div className="product-box ratio_square">
                    <div className="img-part">
                      <a onClick={() => goToProduct(product.id)}>
                        <img
                          src={
                            product?.featuredImage?.src ||
                            "assets/images/products/1000.jpg"
                          }
                          alt={product?.featuredImage?.altText || ""}
                          className="img-fluid bg-img"
                        />
                      </a>
                      <div
                        className={getWishlistClassName(
                          wishlistState[product.id],
                        )}
                        onClick={(event) => toggleWishlist(event, product)}
                        role="button"
                        tabIndex={0}
                        aria-label="Toggle wishlist"
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            toggleWishlist(event, product);
                          }
                        }}
                      >
                        <i className="iconly-Heart icli"></i>
                        <i className="iconly-Heart icbo"></i>
                        {wishlistState[product.id]?.animate && (
                          <div className="effect-group">
                            <span className="effect"></span>
                            <span className="effect"></span>
                            <span className="effect"></span>
                            <span className="effect"></span>
                            <span className="effect"></span>
                          </div>
                        )}
                      </div>
                      {product?.totalInventory > 0 ? (
                        <ProductBadges tags={product?.tags} rotate />
                      ) : (
                        <label>out of stock</label>
                      )}
                    </div>
                    <div
                      onClick={() => goToProduct(product.id)}
                      className="product-content"
                    >
                      <a>
                        <h4>{product?.title}</h4>
                      </a>
                      {showPrice && (
                        <div className="price">
                          {product?.variants?.nodes?.length > 1 ? (
                            <h4>
                              ₹{product?.priceRange?.minVariantPrice?.amount}
                              <span> onwards </span>
                              {/* ₹{product?.priceRange?.maxVariantPrice?.amount} */}
                            </h4>
                          ) : (
                            <h4>
                              ₹{product?.priceRange?.minVariantPrice?.amount}
                            </h4>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        {showLoadMore && <ProductLoader ref={infiniteRef} />}
      </section>
      <div
        className="modal filter-modal"
        id="filterModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Filters</h2>
              <a href="#" data-bs-dismiss="modal">
                <img src="assets/svg/x-dark.svg" className="img-fluid" alt="" />
              </a>
            </div>
            <div className="modal-body">
              <div className="filter-box">
                <h2 className="filter-title">Sort By:</h2>
                <div className="filter-content">
                  <select
                    className="form-select form-control form-theme"
                    aria-label="Default select example"
                  ></select>
                </div>
              </div>
              <div className="filter-box">
                <h2 className="filter-title">Brand:</h2>
                <div className="filter-content">
                  <ul className="row filter-row g-3">
                    <li className="col-6">
                      <div className="filter-col">Here & Now</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">Zara</div>
                    </li>
                    <li className="col-6 active">
                      <div className="filter-col">Mast & harbour</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">Tokyo talkies</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">Vogue</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">gucci</div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="filter-box">
                <h2 className="filter-title">Size:</h2>
                <div className="filter-content">
                  <ul className="row filter-row g-3">
                    <li className="col-4">
                      <div className="filter-col">small</div>
                    </li>
                    <li className="col-4">
                      <div className="filter-col">Medium</div>
                    </li>
                    <li className="col-4">
                      <div className="filter-col">large</div>
                    </li>
                    <li className="col-4">
                      <div className="filter-col">XL</div>
                    </li>
                    <li className="col-4">
                      <div className="filter-col">2XL</div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="filter-box">
                <h2 className="filter-title">Occasion:</h2>
                <div className="filter-content">
                  <ul className="row filter-row g-3">
                    <li className="col-6">
                      <div className="filter-col">Casual</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">sports</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">beach wear</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">lounge wear</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">formal</div>
                    </li>
                    <li className="col-6">
                      <div className="filter-col">party</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <a className="reset-link" data-bs-dismiss="modal">
                RESET
              </a>
              <a className="btn btn-solid" data-bs-dismiss="modal">
                apply filters
              </a>
            </div>
          </div>
        </div>
      </div>
      <section className="panel-space"></section>
    </>
  );
}
